import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';

import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { AccountState } from 'src/app/store/account';

/** Route prefixes the banner applies to — the two partner-scoped work surfaces. */
const STAFF_BANNER_ROUTE_PATTERN = /^\/(notebook|admin)(\/|$)/;

/**
 * Persistent, non-dismissible pill shown while a Blume staff user is working
 * inside a district they are NOT a member of (staff access).
 *
 * In that mode `AccountState.activeProfile()` is a fabricated in-memory
 * Administrator profile with a hard-coded empty `studentIds`, so caseload
 * membership does not exist and caseload writes cannot take effect. Staff who
 * DO hold a real membership profile in the district get the normal experience
 * and this renders nothing — `isStaffAccessActive()` is false whenever a real
 * profile resolves, because `activeProfile()` always prefers the real doc.
 *
 * Mounted once in `AppComponent` rather than in the notebook/admin shells:
 * roughly seventeen `notebook/*` routes and one `admin/*` route are flat
 * siblings of those shells (see the "sits outside the shell" comments in
 * `app.routes.ts`), so a shell-level mount would silently miss them. Gating on
 * the URL here keeps a single mount authoritative for every current and future
 * route under both prefixes.
 *
 * @feature layout
 * @security FERPA - surfaces that a non-member staff user is viewing a
 * district's student records.
 */
@Component({
  selector: 'app-staff-access-banner',
  templateUrl: './staff-access-banner.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
})
export class StaffAccessBannerComponent {
  // --- Injected dependencies ---
  readonly accountState = inject(AccountState);
  private readonly router = inject(Router);

  // --- Internal state ---

  /** True while the current URL sits under `notebook/*` or `admin/*`. */
  private readonly onPartnerSurface = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => STAFF_BANNER_ROUTE_PATTERN.test(event.urlAfterRedirects)),
      startWith(STAFF_BANNER_ROUTE_PATTERN.test(this.router.url)),
    ),
    { initialValue: STAFF_BANNER_ROUTE_PATTERN.test(this.router.url) },
  );

  /** Show only for staff access, and only on the partner-scoped surfaces. */
  readonly visible = computed(() => this.accountState.isStaffAccessActive() && this.onPartnerSurface());

  /** District name for the pill, falling back to generic copy while the partner doc loads. */
  readonly districtName = computed(() => this.accountState.activeTeam()?.name?.trim() || null);
}
